import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { APP_FULL_TITLE } from "@/lib/brand";

export default function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen bg-background flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#F1EEE7] flex-col justify-between p-14">
        <div className="flex gap-2">
          {["#A8C3A0", "#9DB7C5", "#B7A9C8", "#8FB9B2", "#D6C3A5", "#CFA6AA"].map((c) => (
            <span key={c} className="w-8 h-8 rounded-full" style={{ backgroundColor: c }} />
          ))}
        </div>
        <div>
          <h2 className="font-serif text-5xl leading-[1.1] text-foreground max-w-md">
            A journal with structure for six pillars of a life.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-sm leading-relaxed">
            Home, Finance, Work, Health, Family, Friends — held together by one
            thread: self-expression.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{APP_FULL_TITLE}</p>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-4xl text-foreground">{title}</h1>
          {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-sm text-muted-foreground">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, ...props }) {
  return (
    <label className="block mb-4">
      <span className="block text-sm font-medium text-foreground/80 mb-1.5">{label}</span>
      <input
        {...props}
        className="w-full px-3.5 py-2.5 rounded-md border border-input bg-white/70 text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-foreground/40 focus:ring-1 focus:ring-foreground/20 transition-colors duration-200"
      />
    </label>
  );
}

export function SubmitButton({ loading, children, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-60 transition-colors duration-200"
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}

export function ErrorNote({ message }) {
  if (!message) return null;
  return (
    <div
      className="mb-4 px-3.5 py-2.5 rounded-md bg-destructive/10 text-destructive text-sm"
      data-testid="auth-error"
    >
      {message}
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.requires_2fa) {
        navigate("/verify-2fa", { state: { tempToken: res.temp_token } });
        return;
      }
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to continue your weekly reflection."
      footer={
        <div className="flex items-center justify-between">
          <Link to="/forgot-password" className="hover:text-foreground" data-testid="forgot-link">
            Forgot password?
          </Link>
          <Link to="/register" className="hover:text-foreground" data-testid="register-link">
            Create account
          </Link>
        </div>
      }
    >
      <form onSubmit={submit} data-testid="login-form">
        <ErrorNote message={error} />
        <Field
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="login-email"
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          data-testid="login-password"
        />
        <SubmitButton loading={loading} data-testid="login-submit">
          Sign in
        </SubmitButton>
      </form>
    </AuthShell>
  );
}
